import { fmtBRL, fmtNumber } from "../../lib/format";
import {
  buildVariation, comparisonPeriod, countNoun, groupNouns, periodLabel, upIsBad,
  type Comparison, type Contribution,
} from "../../lib/calculation-memory/memory-tabs";
import { useMemoryAllGroups } from "./memory-data";
import { IconArrowDown, IconArrowUp, IconMinus } from "../icons";

/**
 * "Por que variou": o mesmo número em dois períodos, lido do mesmo endpoint da
 * memória de cálculo. Mostra a ponte entre os dois totais (clientes que
 * cresceram, que reduziram, novos e os que não voltaram), quantidade × valor
 * médio e quem mais puxou a variação.
 */
export function MemoryVariationTab({
  cardKey, displayName, unit, from, to, comparison, onComparison,
}: {
  cardKey: string;
  displayName: string;
  unit: "BRL" | "number";
  from: string;
  to: string;
  comparison: Comparison;
  onComparison: (c: Comparison) => void;
}) {
  const base = comparisonPeriod(from, to, comparison);
  // Nome curto de cada período para as legendas ("1 a 16 de set."). Na comparação com o
  // ano anterior o ano fica, senão os dois rótulos sairiam iguais.
  const short = (a: string, b: string) => { const s = periodLabel(a, b); return comparison === "ano" ? s : s.replace(/ de \d{4}$/, ""); };
  const baseShort = short(base.from, base.to);
  const nowShort = short(from, to);
  const now = useMemoryAllGroups(cardKey, from, to);
  const before = useMemoryAllGroups(cardKey, base.from, base.to);

  const fmt = (v: number) => (unit === "number" ? fmtNumber(Math.round(v)) : fmtBRL(v));
  const fmtShort = (v: number) => (unit === "number" ? fmtNumber(Math.round(v)) : compactBRL(v));
  const bad = upIsBad(cardKey);

  const header = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-sm text-gray-600">
        Comparando <span className="font-semibold text-gray-900">{periodLabel(from, to)}</span> com{" "}
        <span className="font-semibold text-gray-900">{periodLabel(base.from, base.to)}</span>
      </p>
      <div className="inline-flex rounded-lg bg-gray-100 p-1 text-xs font-medium" role="group" aria-label="Comparar com">
        {([["anterior", "Período anterior"], ["ano", "Mesmo período do ano anterior"]] as const).map(([k, label]) => (
          <button
            key={k}
            type="button"
            aria-pressed={comparison === k}
            onClick={() => onComparison(k)}
            className={`rounded-md px-3 py-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-tm-500 ${
              comparison === k ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );

  if (now.isLoading || before.isLoading) {
    return (
      <div className="space-y-4">
        {header}
        <div className="card p-6 text-sm text-gray-600 animate-pulse">Lendo os dois períodos…</div>
      </div>
    );
  }
  if (now.isError || before.isError || !now.data || !before.data) {
    return (
      <div className="space-y-4">
        {header}
        <div className="card p-6 text-sm text-gray-700">Não foi possível carregar o período de comparação. Tente de novo em instantes.</div>
      </div>
    );
  }

  const nouns = groupNouns(now.data.breakdown.groupBy);
  const v = buildVariation({
    before: { total: before.data.value.total, count: before.data.value.recordCount, rows: before.data.breakdown.rows },
    now: { total: now.data.value.total, count: now.data.value.recordCount, rows: now.data.breakdown.rows },
  });
  const delta = v.now - v.before;
  const pct = v.before !== 0 ? (delta / Math.abs(v.before)) * 100 : null;
  const showAverage = unit === "BRL" && v.volumeEffect != null && v.averageEffect != null;

  // Ponte entre os dois totais
  const steps: Array<{ label: string; value: number; note?: string }> = [
    { label: `${cap(nouns.many)} que cresceram`, value: v.grew },
    { label: `${cap(nouns.many)} que reduziram`, value: v.shrank },
    { label: `Novos ${nouns.many}`, value: v.added, note: v.addedCount ? `${fmtNumber(v.addedCount)}` : undefined },
    { label: `${cap(nouns.many)} sem movimento`, value: v.lost, note: v.lostCount ? `${fmtNumber(v.lostCount)}` : undefined },
  ];
  if (Math.abs(v.syncGap) >= Math.max(1, Math.abs(v.now) * 0.0005)) {
    steps.push({ label: "Diferença entre ERP e detalhamento", value: v.syncGap });
  }

  const biggest = [...steps].filter((s) => Math.abs(s.value) > 0).sort((a, b) => Math.abs(b.value) - Math.abs(a.value)).slice(0, 2);
  const verb = delta > 0 ? "subiu" : delta < 0 ? "caiu" : "ficou estável";

  return (
    <div className="space-y-4">
      {header}

      {/* Resumo em uma frase */}
      <div className="card px-5 py-4">
        <p className="text-sm text-gray-800 leading-relaxed">
          {displayName} {verb}
          {delta !== 0 && <> <strong className="tabular-nums">{fmt(Math.abs(delta))}</strong>{pct != null && <> ({fmtSignedPct(pct)})</>}</>}
          , de {fmt(v.before)} para {fmt(v.now)}.
          {biggest.length > 0 && delta !== 0 && (
            <> O que mais pesou: {biggest.map((s, i) => (
              <span key={s.label}>{i > 0 ? " e " : ""}{s.label.toLowerCase()} ({signed(s.value, fmtShort)})</span>
            ))}.</>
          )}
        </p>
      </div>

      {/* Período atual × período de comparação */}
      <div className={`grid grid-cols-1 gap-3 ${showAverage ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
        <CompareTile label={displayName} before={fmt(v.before)} baseLabel={baseShort} now={fmt(v.now)} pct={pct} bad={bad} />
        <CompareTile
          label={`Quantidade de ${countNoun(cardKey, 2)}`}
          before={fmtNumber(v.countBefore)} baseLabel={baseShort} now={fmtNumber(v.countNow)}
          pct={v.countBefore ? ((v.countNow - v.countBefore) / v.countBefore) * 100 : null}
          bad={false}
        />
        {showAverage && (
          <CompareTile
            label={`Valor médio por ${countNoun(cardKey, 1)}`}
            before={fmtBRL(v.before / v.countBefore)} baseLabel={baseShort} now={fmtBRL(v.now / v.countNow)}
            pct={((v.now / v.countNow) / (v.before / v.countBefore) - 1) * 100}
            bad={bad}
          />
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        {/* Ponte */}
        <section className="card p-5 xl:col-span-3" aria-labelledby="ponte-titulo">
          <h3 id="ponte-titulo" className="text-sm font-semibold text-gray-900">De {baseShort} para {nowShort}</h3>
          <p className="text-xs text-gray-500 mt-0.5">Cada linha soma ou tira do total anterior até chegar ao atual.</p>
          <Bridge before={v.before} now={v.now} beforeLabel={baseShort} nowLabel={nowShort} steps={steps} fmt={fmtShort} bad={bad} />
        </section>

        {/* Quantidade × valor médio */}
        <section className="card p-5 xl:col-span-2" aria-labelledby="qtd-medio-titulo">
          <h3 id="qtd-medio-titulo" className="text-sm font-semibold text-gray-900">Quantidade × valor médio</h3>
          {showAverage ? (
            <>
              <p className="text-xs text-gray-500 mt-0.5">A mesma variação, separada em dois efeitos que somam o total.</p>
              <dl className="mt-4 space-y-4 text-sm">
                <Effect
                  title={`${v.countNow >= v.countBefore ? "Mais" : "Menos"} ${countNoun(cardKey, 2)}`}
                  detail={`${signedNumber(v.countNow - v.countBefore)} × ${fmtBRL(v.before / v.countBefore)} (valor médio de ${baseShort})`}
                  value={v.volumeEffect!} fmt={fmtShort} bad={bad}
                />
                <Effect
                  title={`Valor médio por ${countNoun(cardKey, 1)}`}
                  detail={`${signed(v.now / v.countNow - v.before / v.countBefore, fmtBRL)} × ${fmtNumber(v.countNow)} ${countNoun(cardKey, v.countNow)}`}
                  value={v.averageEffect!} fmt={fmtShort} bad={bad}
                />
                <div className="flex items-baseline justify-between border-t border-surface-border pt-3">
                  <dt className="font-semibold text-gray-900">Variação total</dt>
                  <dd className="font-semibold tabular-nums text-gray-900">{signed(delta, fmtShort)}</dd>
                </div>
              </dl>
            </>
          ) : (
            <p className="text-sm text-gray-600 mt-2">
              Este número já é uma quantidade, então não há valor médio para separar.
            </p>
          )}
        </section>
      </div>

      {/* Quem mais puxou */}
      <section className="card p-5" aria-labelledby="puxou-titulo">
        <h3 id="puxou-titulo" className="text-sm font-semibold text-gray-900">Quem mais puxou a variação</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Os {nouns.many} com a maior diferença. Em cada linha, o valor de {baseShort} → o de {nowShort}
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6 mt-4">
          <MoversList title="Mais cresceram" items={v.risers} fmt={fmtShort} bad={bad} empty={`Nenhum ${nouns.one} cresceu.`} />
          <MoversList title="Mais caíram" items={v.fallers} fmt={fmtShort} bad={bad} empty={`Nenhum ${nouns.one} caiu.`} />
        </div>
      </section>
    </div>
  );
}

// ── peças ────────────────────────────────────────────────────────────────────

function CompareTile({ label, before, baseLabel, now, pct, bad }: { label: string; before: string; baseLabel: string; now: string; pct: number | null; bad: boolean }) {
  return (
    <div className="card px-4 py-3.5">
      <p className="text-xs font-medium text-gray-600">{label}</p>
      <p className="font-display font-semibold text-2xl text-gray-900 tabular-nums mt-0.5 truncate">{now}</p>
      <p className="flex flex-wrap items-center gap-1.5 mt-1 text-xs text-gray-600">
        {pct != null && <DeltaChip pct={pct} bad={bad} />}
        <span>frente a <span className="font-medium text-gray-800 tabular-nums">{before}</span> em {baseLabel}</span>
      </p>
    </div>
  );
}

function DeltaChip({ pct, bad }: { pct: number; bad: boolean }) {
  const flat = Math.abs(pct) < 0.05;
  const good = flat ? null : (pct > 0) !== bad;
  const tone = good == null ? "bg-gray-100 text-gray-700" : good ? "bg-tm-50 text-tm-700" : "bg-danger-50 text-danger-600";
  const Icon = flat ? IconMinus : pct > 0 ? IconArrowUp : IconArrowDown;
  return (
    <span className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 font-semibold tabular-nums ${tone}`}>
      <Icon className="w-3 h-3" />
      {Math.abs(pct).toFixed(1).replace(".", ",")}%
    </span>
  );
}

/** Ponte horizontal: total anterior → efeitos → total atual. */
function Bridge({
  before, now, beforeLabel, nowLabel, steps, fmt, bad,
}: { before: number; now: number; beforeLabel: string; nowLabel: string; steps: Array<{ label: string; value: number; note?: string }>; fmt: (v: number) => string; bad: boolean }) {
  // Escala com zoom: os totais são grandes e as diferenças pequenas; a barra dos
  // totais começa no menor ponto da ponte (a escala não parte do zero).
  let run = before;
  const points = [before];
  for (const s of steps) { run += s.value; points.push(run); }
  points.push(now);
  const lo0 = Math.min(...points);
  const hi0 = Math.max(...points);
  const pad = (hi0 - lo0) * 0.15 || Math.abs(hi0) * 0.05 || 1;
  const lo = lo0 - pad;
  const hi = hi0 + pad;
  const X = (v: number) => ((v - lo) / (hi - lo)) * 100;

  let acc = before;
  const rows = [
    { label: beforeLabel, note: undefined as string | undefined, kind: "total" as const, a: lo, b: before, value: before },
    ...steps.map((s) => {
      const a = acc;
      acc += s.value;
      return { label: s.label, note: s.note, kind: "step" as const, a: Math.min(a, acc), b: Math.max(a, acc), value: s.value };
    }),
    { label: nowLabel, note: undefined as string | undefined, kind: "total" as const, a: lo, b: now, value: now },
  ];

  return (
    <ul className="mt-4 space-y-2">
      {rows.map((r) => {
        const good = r.kind === "step" && r.value !== 0 ? (r.value > 0) !== bad : null;
        const fill = r.kind === "total" ? "bg-gray-300" : good ? "bg-tm-500" : good === false ? "bg-danger-500" : "bg-gray-200";
        return (
          <li key={r.label} className="grid grid-cols-[minmax(0,12.5rem)_1fr_6.5rem] items-center gap-3 text-sm">
            <span className={`truncate ${r.kind === "total" ? "font-semibold text-gray-900" : "text-gray-700"}`} title={r.label}>
              {r.label}{r.note ? <span className="text-gray-500"> ({r.note})</span> : null}
            </span>
            <span className="relative h-4 rounded bg-gray-50" aria-hidden="true">
              <span
                className={`absolute inset-y-0 rounded ${fill}`}
                style={{ left: `${X(r.a)}%`, width: `${Math.max(0.6, X(r.b) - X(r.a))}%` }}
              />
            </span>
            <span className={`text-right tabular-nums ${r.kind === "total" ? "font-semibold text-gray-900" : good ? "text-tm-700" : good === false ? "text-danger-600" : "text-gray-500"}`}>
              {r.kind === "total" ? fmt(r.value) : signed(r.value, fmt)}
            </span>
          </li>
        );
      })}
      <li className="text-[11px] text-gray-500 pt-1">A escala começa perto do menor valor da ponte, para as diferenças ficarem visíveis.</li>
    </ul>
  );
}

function Effect({ title, detail, value, fmt, bad }: { title: string; detail: string; value: number; fmt: (v: number) => string; bad: boolean }) {
  const good = value === 0 ? null : (value > 0) !== bad;
  return (
    <div className="flex items-baseline justify-between gap-3">
      <div className="min-w-0">
        <dt className="font-medium text-gray-900">{title}</dt>
        <dd className="text-xs text-gray-500 tabular-nums">{detail}</dd>
      </div>
      <span className={`whitespace-nowrap font-semibold tabular-nums ${good ? "text-tm-700" : good === false ? "text-danger-600" : "text-gray-700"}`}>
        {signed(value, fmt)}
      </span>
    </div>
  );
}

function MoversList({
  title, items, fmt, bad, empty,
}: { title: string; items: Contribution[]; fmt: (v: number) => string; bad: boolean; empty: string }) {
  const max = Math.max(1, ...items.map((c) => Math.abs(c.delta)));
  return (
    <div>
      <h4 className="text-xs font-semibold text-gray-600 mb-2">{title}</h4>
      {items.length === 0 ? (
        <p className="text-sm text-gray-500">{empty}</p>
      ) : (
        <ol className="space-y-2.5">
          {items.map((c) => {
            const good = (c.delta > 0) !== bad;
            return (
              <li key={c.key} className="text-sm">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="truncate text-gray-900" title={c.label}>{c.label}</span>
                  <span className={`flex-shrink-0 font-semibold tabular-nums ${good ? "text-tm-700" : "text-danger-600"}`}>{signed(c.delta, fmt)}</span>
                </div>
                <div className="flex items-center gap-3 mt-1">
                  <span className="relative flex-1 h-1.5 rounded-full bg-gray-100" aria-hidden="true">
                    <span className={`absolute inset-y-0 left-0 rounded-full ${good ? "bg-tm-500" : "bg-danger-500"}`} style={{ width: `${(Math.abs(c.delta) / max) * 100}%` }} />
                  </span>
                  <span className="flex-shrink-0 text-xs text-gray-500 tabular-nums">
                    {c.before === 0 ? "sem movimento" : fmt(c.before)} → {c.now === 0 ? "sem movimento" : fmt(c.now)}
                  </span>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

// ── formatação ───────────────────────────────────────────────────────────────

function cap(s: string) { return s.charAt(0).toUpperCase() + s.slice(1); }

function signed(v: number, fmt: (v: number) => string) {
  if (Math.abs(v) < 0.005) return fmt(0);
  return `${v > 0 ? "+" : "−"}${fmt(Math.abs(v))}`;
}

function signedNumber(v: number) {
  return `${v > 0 ? "+" : v < 0 ? "−" : ""}${fmtNumber(Math.abs(v))}`;
}

function fmtSignedPct(p: number) {
  return `${p > 0 ? "+" : p < 0 ? "−" : ""}${Math.abs(p).toFixed(1).replace(".", ",")}%`;
}

/** "R$ 2,81 mi" / "R$ 815 mil" — para listas e a ponte. */
export function compactBRL(v: number): string {
  const a = Math.abs(v);
  const s = v < 0 ? "-" : "";
  if (a >= 1_000_000) return `${s}R$ ${(a / 1_000_000).toFixed(2).replace(".", ",")} mi`;
  if (a >= 10_000) return `${s}R$ ${Math.round(a / 1_000)} mil`;
  return fmtBRL(v);
}
